import { NextResponse } from "next/server";

import { apiError } from "@/lib/api/errors";

/**
 * The Upscalr → AI Suite shutdown, in one place.
 *
 * Image upscaling moved to AI Suite. Accounts and credits were migrated ahead
 * of this change, so nothing here creates users, moves balances or touches
 * billing — the only job left is to stop the old workflow and point people at
 * the new one.
 *
 * Enforced in `proxy.ts` for the same reason maintenance mode is: it is the
 * single layer both pages and API routes pass through, so "this is retired" is
 * stated once instead of at the top of five handlers, where the sixth one
 * added later silently keeps working.
 *
 * ## What is deliberately still up
 *
 * A shutdown that also locks people out of their own data is a support queue,
 * not a migration. Everything that lets someone *leave* keeps working:
 * sign-in, history, job downloads, the batch zip, the privacy centre and its
 * data export. The worker route stays up too, so jobs that were already
 * running when this shipped still finish and are still downloadable.
 * Payment webhooks stay up because refunds and settlements arrive on them
 * after the last purchase.
 */

/** Where every call to action on the old site now goes. */
export const AI_SUITE_UPSCALE_URL = "https://aisuite.run/generate/image-upscale";

/**
 * Pages whose only purpose was the retired workflow.
 *
 * Sent to `/` — the migration page — rather than straight to AI Suite. An
 * off-site redirect from a bookmark gives no explanation, and someone who
 * lands on aisuite.run with no idea why cannot tell a migration from a
 * hijacked domain.
 *
 * Prefixes, so `/dashboard/billing/anything` goes the same way as its parent.
 */
export const RETIRED_PAGES = ["/pricing", "/dashboard/billing", "/api/pricing"];

/**
 * Endpoints that would otherwise start new work, by method.
 *
 * Method-scoped on purpose: `GET /api/jobs` lists a customer's history and
 * `GET /api/jobs/:id/download` hands back their result. Retiring the whole
 * prefix would take those with it, which is exactly the "locked out of my own
 * data" failure above.
 */
export const RETIRED_ENDPOINTS: { prefix: string; methods: string[] }[] = [
  { prefix: "/api/jobs", methods: ["POST"] }, // the web uploader
  { prefix: "/api/uploads/presign", methods: ["POST"] },
  { prefix: "/api/v1/upscale", methods: ["POST"] }, // the public API
];

const matches = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * The response a retired route gets, or `null` if the route still works.
 *
 * API callers get 410 rather than 404 or 503: 404 says "check your URL", 503
 * says "retry later", and both send an integrator looking for a bug that does
 * not exist. 410 says the endpoint is gone for good, and the message carries
 * the address of its replacement so the fix is in the error itself.
 */
export function retiredRoute(pathname: string, method: string): NextResponse | null {
  const endpoint = RETIRED_ENDPOINTS.find(
    (entry) => matches(pathname, entry.prefix) && entry.methods.includes(method),
  );

  if (endpoint) {
    return apiError(
      "gone",
      `Upscalr no longer accepts new upscales. Image upscaling has moved to AI Suite: ${AI_SUITE_UPSCALE_URL}. Existing jobs and downloads are unaffected.`,
      { headers: { Link: `<${AI_SUITE_UPSCALE_URL}>; rel="alternate"` } },
    );
  }

  return null;
}

/** Whether a page request should be sent to the migration notice. */
export function isRetiredPage(pathname: string): boolean {
  return RETIRED_PAGES.some((prefix) => matches(pathname, prefix));
}
